// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography, useTheme } from '@mui/material';
import { Link, LinkProps } from '@tanstack/react-router';
import { ReactNode } from 'react';

interface Props extends Pick<LinkProps, 'to' | 'search' | 'params' | 'hash'> {
  /**
   * An icon to display on the left side of the link.
   */
  icon: ReactNode;

  /**
   * The title of the report.
   */
  title: string;

  /**
   * The subtitle of the report.
   */
  subtitle: ReactNode;

  /**
   * If true, the link is disabled.
   */
  isDisabled?: boolean;

  /**
   * The hash to append to the link.
   */
  hash?: string;
}

/**
 * A link to a specific report.
 */
export function ReportLink(props: Props) {
  const theme = useTheme();

  const color = props.isDisabled ? 'disabled' : 'action';

  return (
    <Link
      to={props.to}
      hash={props.hash}
      search={props.search}
      params={props.params}
      style={{ textDecoration: 'none' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        margin={1}
        padding={1}
        borderRadius={1}
        sx={{
          color: theme.palette.text.primary,
          cursor: props.isDisabled ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: props.isDisabled
              ? theme.palette.background.paper
              : theme.palette.action.hover,
          },
        }}
      >
        {props.icon}
        <Stack marginRight="auto" marginLeft={2} paddingRight={4}>
          <Typography variant="subtitle2">{props.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {props.subtitle}
          </Typography>
        </Stack>
        <ArrowForwardRounded fontSize="small" color={color} />
      </Stack>
    </Link>
  );
}
